





















































import { Component, Vue, Prop } from 'vue-property-decorator';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import { translateEnum } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-role';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { TooltipPlacement } from '@getaccept/lib-shared/src/enums/tooltip-placement';
import { ParagraphSize } from '@getaccept/lib-ui';

@Component({
  data: () => ({
    RecipientStatus,
  }),
})
export default class RecipientsTable extends Vue {
  @Prop() recipients: SigningPageRecipient[];
  @Prop() isSigningOrder: boolean;
  @Prop({ default: 3 }) showNumRecipients: number;
  @Prop({ default: 6 }) rowHeight: number;
  @Prop() recipient: SigningPageRecipient;

  TooltipPlacement = TooltipPlacement;
  ParagraphSize = ParagraphSize;

  get recipientsTitle(): string {
    return `${this.$t('recipients').toString()} (${this.recipients.length})`;
  }

  get recipientsOrderSorted(): SigningPageRecipient[] {
    return [
      ...this.recipients,
    ].sort((recipient1: SigningPageRecipient, recipient2: SigningPageRecipient) =>
      recipient1.orderNum < recipient2.orderNum ? -1 : 1
    );
  }

  name(recipient: SigningPageRecipient): string {
    if (recipient.fullName) {
      return recipient.fullName;
    } else if (recipient.email) {
      return recipient.email;
    } else {
      return recipient.mobile;
    }
  }

  checked(recipient: SigningPageRecipient): boolean {
    if (recipient.role === RecipientRole.CC && recipient.status === RecipientStatus.Reviewed) {
      return true;
    }
    const checkedStatuses: RecipientStatus[] = [RecipientStatus.Signed, RecipientStatus.Approved];
    return checkedStatuses.some((status: RecipientStatus) => status === recipient.status);
  }

  title(recipient: SigningPageRecipient): string {
    const { title, companyName } = recipient;
    if (title && companyName) {
      return `${title} ${this.$t('role_at')} ${companyName}`;
    }
    if (title) {
      return title;
    }
    if (companyName) {
      return companyName;
    }
  }

  role(recipient: SigningPageRecipient): string {
    switch (recipient.role) {
      case RecipientRole.CC:
        return this.$t('recipient-role-cc').toString();
      case RecipientRole.Signer:
        return this.$t('recipient-role-signer').toString();
      case RecipientRole.ExternalApprover:
        if (this.recipient.role === RecipientRole.Approver) {
          return this.$t('recipient-role-external-approver').toString();
        }
        return this.$t('recipient-role-approver').toString();
      case RecipientRole.Approver:
        if (this.recipient.role === RecipientRole.Approver) {
          return this.$t('recipient-role-internal-approver').toString();
        }
        return this.$t('recipient-role-approver').toString();
      default:
        return '-';
    }
  }

  getRoleTooltip(role: RecipientRole) {
    switch (role) {
      case RecipientRole.Signer:
        /* eslint-disable sonarjs/no-duplicate-string */
        return `
          <li>${this.$t('signer-role-description-bullet-one')}</li>
          <li>${this.$t('recipient-role-description-visible-bullet')}</li>
          <li>${this.$t('recipient-role-description-certificate-bullet')}</li>
        `;
      case RecipientRole.Approver:
        return `
          <li>${this.$t('internal-approver-role-description-bullet-one')}</li>
          <li>${this.$t('recipient-role-description-not-visible-bullet')}</li>
          <li>${this.$t('recipient-role-description-not-certificate-bullet')}</li>
        `;
      case RecipientRole.ExternalApprover:
        return `
          <li>${this.$t('external-approver-role-description-bullet-one')}</li>
          <li>${this.$t('recipient-role-description-visible-bullet')}</li>
          <li>${this.$t('recipient-role-description-certificate-bullet')}</li>
        `;
      case RecipientRole.CC:
        return `
          <li>${this.$t('viewer-role-description-bullet-one')}</li>
          <li>${this.$t('recipient-role-description-visible-bullet')}</li>
          <li>${this.$t('recipient-role-description-certificate-bullet')}</li>
        `;
    }
  }

  status(recipient: SigningPageRecipient): string {
    return recipient.status
      ? translateEnum(recipient.status, 'recipient-status', RecipientStatus)
      : '-';
  }
}
